import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Component
// ============================================================================

/**
 * Gallery page intro: a short, centered blurb that opens the page below the
 * hero/wave band. Mirrors the reference site's gallery intro — the decorative
 * palm-tree icon that sits above the copy on the reference is intentionally
 * omitted. No heading, just the blurb, so the tabbed photo gallery that
 * follows leads the page.
 */
export function GalleryIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-[var(--vibe-color-background)] pt-[40px] pb-0"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-16 lg:px-8 text-center">
        <RichTextContent
          path="galleryintro.body"
          label="Body Text"
          defaultValue='<p>Browse our gallery to see the beautiful apartments and resort-style amenities at <span data-type="field-token" data-field="property_name"></span>. Discover why our community is the perfect place to call home.</p>'
          as="div"
          className="font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_p:last-child]:mb-0 [&_a]:text-primary [&_a]:underline"
        />
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryIntro',
  displayName: 'Gallery Intro',
  tokens: {
    'galleryintro.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_gallery_blurb',
      defaultValue:
        '<p>Browse our gallery to see the beautiful apartments and resort-style amenities at <span data-type="field-token" data-field="property_name"></span>. Discover why our community is the perfect place to call home.</p>',
    },
  },
};

(GalleryIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default GalleryIntro;
